import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  Download,
  Eye,
  Trash2,
  MoreVertical,
  Filter,
  Calendar,
  X,
  FileText,
  CheckSquare,
  Square,
  AlertTriangle,
  Upload,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Clock,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatInvoiceDate } from "@/utils/dateFormat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseInvoiceDateForFilter = (value) => {
  if (!value) return null;

  const normalizedValue = String(value).trim();

  const ddmmyyyyMatch = normalizedValue.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/,
  );
  if (ddmmyyyyMatch) {
    let [, day, month, year] = ddmmyyyyMatch;
    if (year.length === 2) {
      year = `20${year}`;
    }
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      0,
      0,
      0,
      0,
    );
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const isoMatch = normalizedValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      0,
      0,
      0,
      0,
    );
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const fallbackParsed = new Date(normalizedValue);
  return Number.isNaN(fallbackParsed.getTime()) ? null : fallbackParsed;
};

export default function InvoiceList() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    purchase: 0,
    sales: 0,
    pending: 0,
    verified: 0,
    totalAmount: 0,
  });

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load invoices");
      setLoading(false);
    }
  };

  const calculateStats = useCallback(() => {
    const total = filteredInvoices.length;

    const purchase = filteredInvoices.filter(
      (inv) => inv.invoice_type === "purchase",
    ).length;

    const sales = filteredInvoices.filter(
      (inv) => inv.invoice_type === "sales",
    ).length;

    const pending = filteredInvoices.filter(
      (inv) => inv.status === "pending",
    ).length;

    const verified = filteredInvoices.filter(
      (inv) => inv.status === "verified",
    ).length;

    const totalAmount = filteredInvoices.reduce(
      (sum, inv) => sum + (inv.extracted_data?.total_amount || 0),
      0,
    );

    setStats({ total, purchase, sales, pending, verified, totalAmount });
  }, [filteredInvoices]);

  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  const filterInvoices = useCallback(() => {
    let filtered = invoices;

    // Filter by invoice type
    if (typeFilter !== "all") {
      filtered = filtered.filter((inv) => inv.invoice_type === typeFilter);
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((inv) => inv.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (inv) =>
          inv.extracted_data?.supplier_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          inv.extracted_data?.buyer_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          inv.extracted_data?.invoice_no
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          inv.filename?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Filter by date range
    if (startDate || endDate) {
      const start = startDate ? parseInvoiceDateForFilter(startDate) : null;
      const end = endDate ? parseInvoiceDateForFilter(endDate) : null;

      if (end) {
        end.setHours(23, 59, 59, 999);
      }

      filtered = filtered.filter((inv) => {
        const invoiceDate = parseInvoiceDateForFilter(
          inv.extracted_data?.invoice_date,
        );
        if (!invoiceDate) return false;

        if (start && invoiceDate < start) return false;
        if (end && invoiceDate > end) return false;
        return true;
      });
    }

    setFilteredInvoices(filtered);
  }, [invoices, searchTerm, statusFilter, typeFilter, startDate, endDate]);

  useEffect(() => {
    filterInvoices();
  }, [filterInvoices]);

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTypeFilter("all");
    setStartDate("");
    setEndDate("");
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?"))
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Invoice deleted successfully");
      loadInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const handleDeleteAll = async () => {
    const firstConfirm = window.confirm(
      `⚠️ WARNING: You are about to delete ALL ${invoices.length} invoice(s)!\n\nThis action cannot be undone.\n\nAre you sure you want to continue?`,
    );

    if (!firstConfirm) return;

    const confirmText = window.prompt(
      `To confirm deletion of ALL invoices, please type: DELETE\n\n(Type DELETE in capital letters)`,
    );

    if (confirmText !== "DELETE") {
      toast.error("Deletion cancelled. Confirmation text did not match.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(
        `Successfully deleted ${response.data.deleted_count} invoice(s)`,
      );
      loadInvoices();
      setSelectedInvoices([]);
    } catch (error) {
      toast.error("Failed to delete invoices");
    }
  };

  const handleExport = async (format) => {
    if (selectedInvoices.length === 0) {
      toast.error("Please select invoices to export");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/invoices/export`,
        { invoice_ids: selectedInvoices, format },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data.data;
      const blob = new Blob([data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoices_export_${Date.now()}.${format === "tally" ? "xml" : format}`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(
        `Exported ${selectedInvoices.length} invoices as ${format.toUpperCase()}`,
      );
      setSelectedInvoices([]);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const toggleSelectInvoice = (invoiceId) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId],
    );
  };

  const toggleSelectAll = () => {
    if (selectedInvoices.length === filteredInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map((inv) => inv.id));
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return "bg-green-500";
    if (confidence >= 0.7) return "bg-orange-500";
    return "bg-red-500";
  };

  const getConfidenceTextColor = (confidence) => {
    if (confidence >= 0.9) return "text-green-600";
    if (confidence >= 0.7) return "text-orange-600";
    return "text-red-600";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "verified":
        return "bg-gradient-to-r from-green-500/10 to-green-500/5 border-l-2 border-green-500 text-green-700";
      case "pending":
        return "bg-gradient-to-r from-orange-500/10 to-orange-500/5 border-l-2 border-orange-500 text-orange-700";
      case "exported":
        return "bg-gradient-to-r from-primary-500/10 to-primary-500/5 border-l-2 border-primary-500 text-primary-700";
      default:
        return "bg-gradient-to-r from-gray-100/50 to-gray-50/50 border-l-2 border-gray-300 text-gray-700";
    }
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })}`;
  };

  const getGstBreakdownLabel = (invoice) => {
    const data = invoice.extracted_data || {};
    if ((data.igst || 0) > 0) {
      return `IGST ${formatCurrency(data.igst || 0)}`;
    }
    return `CGST ${formatCurrency(data.cgst || 0)} | SGST ${formatCurrency(data.sgst || 0)}`;
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="invoice-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
                  Invoice Manager
                </h1>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {filteredInvoices.length} of {invoices.length} invoices
              </p>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2">
            <div className="flex gap-2">
              {invoices.length > 0 && (
                <Button
                  onClick={handleDeleteAll}
                  data-testid="delete-all-btn"
                  variant="destructive"
                  className="h-7 text-xs bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                >
                  <Trash2 size={10} className="mr-1" />
                  Delete All
                </Button>
              )}
              <Button
                onClick={() => navigate("/")}
                data-testid="back-to-dashboard-btn"
                className="h-7 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white"
              >
                <Upload size={10} className="mr-1" />
                Upload
              </Button>
            </div>
          </div>
        </div>

        {/* Filters and Actions - Enhanced */}
        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardContent className="p-4">
            <div className="space-y-4">
              {/* Header Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                    <Filter size={14} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">
                      Filters & Actions
                    </h3>
                    <p className="text-xs text-gray-500">
                      Refine your invoice list or perform bulk operations
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs bg-white">
                    {selectedInvoices.length} selected
                  </Badge>
                  {selectedInvoices.length > 0 && (
                    <Badge className="text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                      Ready to export
                    </Badge>
                  )}
                </div>
              </div>

              {/* Search and Quick Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search Input */}
                <div className="flex-1 relative group">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500 transition-colors"
                    size={16}
                  />
                  <Input
                    placeholder="Search invoices by name, number, or GST..."
                    data-testid="search-invoices-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-9 text-sm border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Type and Status Filters */}
                <div className="flex gap-2">
                  <div className="relative">
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger
                        className="w-40 h-9 text-sm border-gray-300 data-[state=open]:border-primary-400"
                        data-testid="type-filter-select"
                      >
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-gray-500" />
                          <SelectValue placeholder="Invoice Type" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="min-w-[200px]">
                        <SelectItem value="all" className="text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                            All Types
                          </div>
                        </SelectItem>
                        <SelectItem value="purchase" className="text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            Purchase Only
                          </div>
                        </SelectItem>
                        <SelectItem value="sales" className="text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            Sales Only
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="relative">
                    <Select
                      value={statusFilter}
                      onValueChange={setStatusFilter}
                    >
                      <SelectTrigger
                        className="w-40 h-9 text-sm border-gray-300 data-[state=open]:border-primary-400"
                        data-testid="status-filter-select"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
                            <Filter size={10} className="text-gray-500" />
                          </div>
                          <SelectValue placeholder="Status" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="min-w-[200px]">
                        <SelectItem value="all" className="text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                            All Status
                          </div>
                        </SelectItem>
                        <SelectItem value="pending" className="text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                            Pending
                          </div>
                        </SelectItem>
                        <SelectItem value="verified" className="text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            Verified
                          </div>
                        </SelectItem>
                        <SelectItem value="exported" className="text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                            Exported
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Date Range and Action Buttons */}
              <div className="flex flex-col gap-4">
                {/* Date Range Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar
                      size={14}
                      className="text-gray-500 hidden sm:block"
                    />
                    <Calendar size={12} className="text-gray-500 sm:hidden" />
                    <Label className="text-sm font-medium text-gray-700">
                      Date Range
                    </Label>
                  </div>

                  <div className="space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                    {/* Start Date */}
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 mt-1 block">
                        From
                      </Label>
                      <div className="relative group">
                        <Input
                          type="date"
                          data-testid="start-date-filter"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="h-9 w-full text-sm border-gray-300 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 appearance-none [-webkit-appearance:none]"
                        />
                        {startDate && (
                          <button
                            onClick={() => setStartDate("")}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-white p-1 rounded"
                          >
                            <X size={12} className="sm:w-3.5 sm:h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Separator - Hidden on mobile, shown on desktop */}
                    <div className="hidden sm:flex items-center">
                      <div className="w-4 h-px bg-gray-300"></div>
                      <div className="mx-2 text-xs text-gray-400">to</div>
                      <div className="w-4 h-px bg-gray-300"></div>
                    </div>

                    {/* End Date */}
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 mt-1 block">
                        To
                      </Label>
                      <div className="relative group">
                        <Input
                          type="date"
                          data-testid="end-date-filter"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="h-9 w-full text-sm border-gray-300 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 appearance-none [-webkit-appearance:none]"
                        />
                        {endDate && (
                          <button
                            onClick={() => setEndDate("")}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-white p-1 rounded"
                          >
                            <X size={12} className="sm:w-3.5 sm:h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons Section */}
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  {/* Clear Filters Button - Only shown when filters are active */}
                  {(searchTerm ||
                    statusFilter !== "all" ||
                    typeFilter !== "all" ||
                    startDate ||
                    endDate) && (
                    <Button
                      variant="outline"
                      onClick={clearFilters}
                      data-testid="clear-filters-btn"
                      className="h-9 px-3 sm:px-4 text-xs sm:text-sm border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 flex-1 sm:flex-none flex items-center justify-center"
                    >
                      <X className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5" />
                      Clear Filters
                    </Button>
                  )}

                  {/* Export and Selection Actions */}
                  {selectedInvoices.length > 0 ? (
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedInvoices([])}
                        className="h-9 px-3 text-xs sm:text-sm border-gray-300 text-gray-600 hover:bg-gray-50 flex-1 sm:flex-none order-2 sm:order-1 flex items-center justify-center"
                      >
                        <X className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5" />
                        Clear Selection
                      </Button>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            data-testid="export-menu-btn"
                            className="h-9 px-3 sm:px-4 text-xs sm:text-sm bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white shadow-sm hover:shadow flex-1 sm:flex-none order-1 sm:order-2 flex items-center justify-center"
                          >
                            <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5" />
                            Export ({selectedInvoices.length})
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-full sm:w-48 shadow-lg"
                        >
                          <div className="px-3 py-2 border-b">
                            <p className="text-xs font-medium text-gray-700">
                              Export Format
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Select export format
                            </p>
                          </div>
                          <DropdownMenuItem
                            onClick={() => handleExport("tally")}
                            data-testid="export-tally-btn"
                            className="text-xs sm:text-sm px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span>Tally XML</span>
                            <Badge variant="outline" className="text-xs">
                              .xml
                            </Badge>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleExport("csv")}
                            data-testid="export-csv-btn"
                            className="text-xs sm:text-sm px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span>CSV File</span>
                            <Badge variant="outline" className="text-xs">
                              .csv
                            </Badge>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleExport("json")}
                            data-testid="export-json-btn"
                            className="text-xs sm:text-sm px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span>JSON Data</span>
                            <Badge variant="outline" className="text-xs">
                              .json
                            </Badge>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    /* Stats/Info when no selection */
                    <div className="text-xs text-gray-500 text-center sm:text-right px-2 py-1.5 bg-gray-50 rounded flex-1 sm:flex-none">
                      {searchTerm ||
                      statusFilter !== "all" ||
                      typeFilter !== "all" ||
                      startDate ||
                      endDate
                        ? `Showing ${filteredInvoices.length} of ${invoices.length} invoices`
                        : `Total: ${filteredInvoices.length} invoices`}
                    </div>
                  )}
                </div>
              </div>

              {/* Active Filters Summary */}
              {(searchTerm ||
                statusFilter !== "all" ||
                typeFilter !== "all" ||
                startDate ||
                endDate) && (
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">
                      Active filters:
                    </span>
                    {searchTerm && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-blue-50 border-blue-200 text-blue-700"
                      >
                        Search: "{searchTerm}"
                        <button
                          onClick={() => setSearchTerm("")}
                          className="ml-1.5 text-blue-500 hover:text-blue-700"
                        >
                          <X size={10} />
                        </button>
                      </Badge>
                    )}
                    {typeFilter !== "all" && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-green-50 border-green-200 text-green-700"
                      >
                        Type: {typeFilter === "purchase" ? "Purchase" : "Sales"}
                        <button
                          onClick={() => setTypeFilter("all")}
                          className="ml-1.5 text-green-500 hover:text-green-700"
                        >
                          <X size={10} />
                        </button>
                      </Badge>
                    )}
                    {statusFilter !== "all" && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-purple-50 border-purple-200 text-purple-700"
                      >
                        Status:{" "}
                        {statusFilter.charAt(0).toUpperCase() +
                          statusFilter.slice(1)}
                        <button
                          onClick={() => setStatusFilter("all")}
                          className="ml-1.5 text-purple-500 hover:text-purple-700"
                        >
                          <X size={10} />
                        </button>
                      </Badge>
                    )}
                    {startDate && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-orange-50 border-orange-200 text-orange-700"
                      >
                        From: {startDate}
                        <button
                          onClick={() => setStartDate("")}
                          className="ml-1.5 text-orange-500 hover:text-orange-700"
                        >
                          <X size={10} />
                        </button>
                      </Badge>
                    )}
                    {endDate && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-orange-50 border-orange-200 text-orange-700"
                      >
                        To: {endDate}
                        <button
                          onClick={() => setEndDate("")}
                          className="ml-1.5 text-orange-500 hover:text-orange-700"
                        >
                          <X size={10} />
                        </button>
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Clear all
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table - Enhanced */}
        <Card className="border-0 bg-gradient-to-br from-white to-gray-50/50 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center" data-testid="loading-message">
                <div className="inline-flex flex-col items-center gap-2">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400/20 to-orange-400/20"></div>
                    <div className="absolute inset-0 rounded-full border-2 border-primary-500/30 border-t-transparent animate-spin"></div>
                  </div>
                  <p className="text-sm text-gray-600">Loading invoices...</p>
                  <p className="text-xs text-gray-400">Please wait a moment</p>
                </div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="p-8">
                <div className="flex flex-col items-center justify-center gap-3">
                  <div className="p-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                    <FileText className="w-6 h-6 text-gray-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-700">
                      No invoices found
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Try adjusting your filters or upload a new invoice
                    </p>
                  </div>
                  {JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" && (
                  <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="mt-2 h-7 text-xs border-gray-300"
                  >
                    <Upload size={10} className="mr-1.5" />
                    Upload Invoice
                  </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Selection Header */}
                  <div className="sticky top-0 z-10 border-b border-gray-200 p-3 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center gap-2 text-xs text-gray-600 hover:text-primary-600 transition-colors"
                        data-testid="select-all-checkbox"
                      >
                        {selectedInvoices.length === filteredInvoices.length ? (
                          <div className="w-4 h-4 rounded bg-gradient-to-br from-primary-500 to-orange-500 flex items-center justify-center">
                            <CheckSquare size={10} className="text-white" />
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded border border-gray-300 hover:border-primary-400"></div>
                        )}
                        <span>
                          {selectedInvoices.length} of {filteredInvoices.length}{" "}
                          selected
                        </span>
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {filteredInvoices.length} invoices
                      </span>
                    </div>
                  </div>

                  {/* Mobile View */}
                  <div className="sm:hidden">
                    {filteredInvoices.map((invoice) => {
                      const avgConfidence =
                        Object.values(invoice.confidence_scores).reduce(
                          (a, b) => a + b,
                          0,
                        ) / 8;
                      return (
                        <div
                          key={invoice.id}
                          data-testid={`invoice-row-${invoice.id}`}
                          className="p-4 border-b border-gray-100 hover:bg-gradient-to-r hover:from-white hover:to-gray-50/50 transition-colors cursor-pointer group"
                          onClick={() => navigate(`/verify/${invoice.id}`)}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSelectInvoice(invoice.id);
                                }}
                                className="shrink-0"
                                data-testid={`select-invoice-${invoice.id}`}
                              >
                                {selectedInvoices.includes(invoice.id) ? (
                                  <div className="w-4 h-4 rounded bg-gradient-to-br from-primary-500 to-orange-500 flex items-center justify-center">
                                    <CheckSquare
                                      size={10}
                                      className="text-white"
                                    />
                                  </div>
                                ) : (
                                  <div className="w-4 h-4 rounded border border-gray-300 group-hover:border-primary-400"></div>
                                )}
                              </button>
                              <Badge
                                className={
                                  invoice.invoice_type === "sales"
                                    ? "bg-gradient-to-br from-green-400 to-green-500 text-white border-0 text-[10px] px-2 py-0"
                                    : "bg-gradient-to-br from-red-400 to-red-500 text-white border-0 text-[10px] px-2 py-0"
                                }
                              >
                                {invoice.invoice_type === "sales"
                                  ? "SALES"
                                  : "PURCHASE"}
                              </Badge>
                              <div
                                className={`px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(invoice.status)}`}
                              >
                                {invoice.status}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/verify/${invoice.id}`);
                                }}
                                className="h-6 w-6 p-0 text-gray-400 hover:text-primary-600"
                              >
                                <Eye size={12} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(invoice.id);
                                }}
                                className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                              >
                                <Trash2 size={12} />
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                                  Invoice No
                                </div>
                                <div className="font-medium text-sm text-gray-800">
                                  {invoice.extracted_data?.invoice_no || "N/A"}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                                  Date
                                </div>
                                <div className="text-sm text-gray-700">
                                  {formatInvoiceDate(
                                    invoice.extracted_data?.invoice_date,
                                  )}
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                                From
                              </div>
                              <div className="font-medium text-sm text-gray-800 truncate">
                                {invoice.extracted_data?.supplier_name || "N/A"}
                              </div>
                            </div>

                            <div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                                To
                              </div>
                              <div className="font-medium text-sm text-gray-800 truncate">
                                {invoice.extracted_data?.buyer_name || "N/A"}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                                  Amount
                                </div>
                                <div className="text-sm font-bold text-primary-600">
                                  {formatCurrency(
                                    invoice.extracted_data?.total_amount || 0,
                                  )}
                                </div>
                                <div className="text-[10px] text-gray-500 mt-0.5">
                                  {getGstBreakdownLabel(invoice)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                                  Confidence
                                </div>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-xs font-medium ${getConfidenceTextColor(avgConfidence)}`}
                                  >
                                    {Math.round(avgConfidence * 100)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop View */}
                  <Table className="hidden sm:table">
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                        <TableHead className="w-10 p-3">
                          <button
                            onClick={toggleSelectAll}
                            className="text-gray-500 hover:text-primary-600 transition-colors"
                            data-testid="select-all-checkbox-desktop"
                          >
                            {selectedInvoices.length ===
                            filteredInvoices.length ? (
                              <div className="w-4 h-4 rounded bg-gradient-to-br from-primary-500 to-orange-500 flex items-center justify-center">
                                <CheckSquare size={10} className="text-white" />
                              </div>
                            ) : (
                              <div className="w-4 h-4 rounded border border-gray-300 hover:border-primary-400"></div>
                            )}
                          </button>
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3">
                          Type
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3">
                          Invoice No
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3">
                          Date
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3">
                          From
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3">
                          To
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3 text-right">
                          Amount
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3 text-center">
                          Confidence
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3 text-center">
                          Status
                        </TableHead>
                        <TableHead className="text-xs text-gray-500 uppercase tracking-wide p-3 text-center">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoices.map((invoice) => {
                        const avgConfidence =
                          Object.values(invoice.confidence_scores).reduce(
                            (a, b) => a + b,
                            0,
                          ) / 8;
                        return (
                          <TableRow
                            key={invoice.id}
                            data-testid={`invoice-row-${invoice.id}`}
                            className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-white hover:to-gray-50/50 cursor-pointer group transition-colors"
                            onClick={() => navigate(`/verify/${invoice.id}`)}
                          >
                            <TableCell
                              className="p-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => toggleSelectInvoice(invoice.id)}
                                className="text-gray-500 hover:text-primary-600 transition-colors"
                                data-testid={`select-invoice-${invoice.id}`}
                              >
                                {selectedInvoices.includes(invoice.id) ? (
                                  <div className="w-4 h-4 rounded bg-gradient-to-br from-primary-500 to-orange-500 flex items-center justify-center">
                                    <CheckSquare
                                      size={10}
                                      className="text-white"
                                    />
                                  </div>
                                ) : (
                                  <div className="w-4 h-4 rounded border border-gray-300 group-hover:border-primary-400"></div>
                                )}
                              </button>
                            </TableCell>
                            <TableCell className="p-3">
                              <Badge
                                className={
                                  invoice.invoice_type === "sales"
                                    ? "bg-gradient-to-br from-green-400 to-green-500 text-white border-0 text-[10px] px-2 py-0"
                                    : "bg-gradient-to-br from-red-400 to-red-500 text-white border-0 text-[10px] px-2 py-0"
                                }
                                data-testid={`type-badge-${invoice.id}`}
                              >
                                {invoice.invoice_type === "sales"
                                  ? "SALES"
                                  : "PURCHASE"}
                              </Badge>
                            </TableCell>
                            <TableCell className="p-3 font-medium text-sm text-gray-800">
                              {invoice.extracted_data?.invoice_no || "N/A"}
                            </TableCell>
                            <TableCell className="p-3 text-sm text-gray-700">
                              {formatInvoiceDate(
                                invoice.extracted_data?.invoice_date,
                              )}
                            </TableCell>
                            <TableCell className="p-3">
                              <div>
                                <div className="font-medium text-sm text-gray-800">
                                  {invoice.extracted_data?.supplier_name ||
                                    "N/A"}
                                </div>
                                {invoice.extracted_data?.supplier_gst_no && (
                                  <div className="text-xs text-gray-500 font-mono mt-0.5">
                                    {invoice.extracted_data.supplier_gst_no}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="p-3">
                              <div>
                                <div className="font-medium text-sm text-gray-800">
                                  {invoice.extracted_data?.buyer_name || "N/A"}
                                </div>
                                {invoice.extracted_data?.buyer_gst_no && (
                                  <div className="text-xs text-gray-500 font-mono mt-0.5">
                                    {invoice.extracted_data.buyer_gst_no}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="p-3 text-right">
                              <div className="text-sm font-bold text-primary-600">
                                {formatCurrency(
                                  invoice.extracted_data?.total_amount || 0,
                                )}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                {getGstBreakdownLabel(invoice)}
                              </div>
                            </TableCell>
                            <TableCell className="p-3">
                              <div className="flex flex-col items-center gap-1">
                                <span
                                  className={`text-xs font-medium ${getConfidenceTextColor(avgConfidence)}`}
                                >
                                  {Math.round(avgConfidence * 100)}%
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="p-3">
                              <div
                                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}
                              >
                                {invoice.status.charAt(0).toUpperCase() +
                                  invoice.status.slice(1)}
                              </div>
                            </TableCell>
                            <TableCell
                              className="p-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/verify/${invoice.id}`)
                                  }
                                  className="h-7 w-7 p-0 text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                                  data-testid={`view-invoice-${invoice.id}`}
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(invoice.id)}
                                  className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                  data-testid={`delete-invoice-${invoice.id}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
